package com.islamicmedia.site.theme

import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.rgb
import org.jetbrains.compose.web.css.rgba

// Primary palette - teal/gold inspired by Fitra Filter logo
val Primary: CSSColorValue = rgb(14, 124, 123)         // Deep teal
val PrimaryLight: CSSColorValue = rgb(20, 163, 160)    // Medium teal
val PrimaryPale: CSSColorValue = rgb(230, 247, 246)    // Pale teal
val Accent: CSSColorValue = rgb(200, 150, 46)          // Warm gold
val AccentLight: CSSColorValue = rgb(255, 248, 235)    // Pale gold

// Plus Jakarta Sans — analysis title font (movie/show/book name), loaded via
// Google Fonts in AppEntry. Kept here so every title render site stays in sync.
val TitleFontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

// Gradient definitions for CSS
val GradientPrimary = "linear-gradient(135deg, #0E7C7B 0%, #14A3A0 50%, #C8962E 100%)"
val NeuBase = "#EBEBEB"   // Flat neumorphic base — light neutral gray
val NeuBoxShadow = "0px 10px 15px rgba(14, 124, 123, 0.25), 0px -6px 10px rgba(230, 247, 246, 0.7), inset 0px 3px 5px rgba(230, 247, 246, 0.9), inset 0px -3px 5px rgba(14, 124, 123, 0.18)"
val CardElevatedShadow = "0 8px 32px rgba(14, 124, 123, 0.18), 0 2px 8px rgba(0, 0, 0, 0.06)"
val GradientHeader = "linear-gradient(180deg, #FFFFFF 0%, transparent 88%), radial-gradient(circle at 100% 100%, #C8962E 0%, transparent 60%), #0E7C7B"
val GradientCard = "#FFFFFF"
val GradientGlow = "radial-gradient(ellipse at 50% 0%, rgba(14, 124, 123, 0.15) 0%, transparent 70%)"

// Score colors - vibrant with depth
val ScoreGreen: CSSColorValue = rgb(34, 197, 94)
val ScoreGreenBg: CSSColorValue = rgb(240, 253, 244)
val ScoreAmber: CSSColorValue = rgb(245, 158, 11)
val ScoreAmberBg: CSSColorValue = rgb(255, 251, 235)
val ScoreRed: CSSColorValue = rgb(239, 68, 68)
val ScoreRedBg: CSSColorValue = rgb(254, 242, 242)

// Severity - softer, more approachable
val SeverityHighBg: CSSColorValue = rgb(254, 242, 242)
val SeverityHighBorder: CSSColorValue = rgb(252, 165, 165)
val SeverityMediumBg: CSSColorValue = rgb(255, 251, 235)
val SeverityMediumBorder: CSSColorValue = rgb(252, 211, 77)
val SeverityLowBg: CSSColorValue = rgb(240, 253, 244)
val SeverityLowBorder: CSSColorValue = rgb(134, 239, 172)

// Quran reference box - calming teal
val QuranBg: CSSColorValue = rgb(240, 253, 250)
val QuranBorder: CSSColorValue = rgb(94, 234, 212)
val QuranTextDark: CSSColorValue = rgb(17, 94, 89)

// Hadith reference box - warm amber
val HadithBg: CSSColorValue = rgb(255, 251, 235)
val HadithBorder: CSSColorValue = rgb(252, 211, 77)
val HadithTextDark: CSSColorValue = rgb(146, 64, 14)

// Positive aspects
val PositiveBg: CSSColorValue = rgb(240, 253, 244)
val PositiveBorder: CSSColorValue = rgb(134, 239, 172)
val PositiveText: CSSColorValue = rgb(22, 101, 52)

// Developmental perspective
val DevelopmentalBg: CSSColorValue = rgb(243, 232, 255)
val DevelopmentalText: CSSColorValue = rgb(124, 58, 237)

// Neutrals - softer grays
val Gray50: CSSColorValue = rgb(250, 250, 250)
val Gray100: CSSColorValue = rgb(245, 245, 245)
val Gray200: CSSColorValue = rgb(229, 229, 229)
val Gray300: CSSColorValue = rgb(212, 212, 212)
val Gray400: CSSColorValue = rgb(163, 163, 163)
val Gray500: CSSColorValue = rgb(115, 115, 115)
val Gray600: CSSColorValue = rgb(82, 82, 82)
val Gray700: CSSColorValue = rgb(64, 64, 64)
val Gray900: CSSColorValue = rgb(23, 23, 23)

// Functional
val CostBg: CSSColorValue = rgb(240, 253, 244)
val CostText: CSSColorValue = rgb(22, 163, 74)
val InfoBg: CSSColorValue = rgb(239, 246, 255)
val InfoBorder: CSSColorValue = rgb(147, 197, 253)
val InfoText: CSSColorValue = rgb(29, 78, 216)

// Background - subtle teal tint
val PageBg: CSSColorValue = rgb(248, 253, 252)

// White with alpha
val WhiteAlpha70: CSSColorValue = rgba(255, 255, 255, 0.7)
val WhiteAlpha90: CSSColorValue = rgba(255, 255, 255, 0.9)

// Card styles - fluid and elevated
val CardShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 20px 40px -10px rgba(14, 124, 123, 0.1)"
val CardShadowHover = "0 10px 20px -5px rgba(0, 0, 0, 0.08), 0 25px 50px -12px rgba(14, 124, 123, 0.15)"
val CardBorder = "1px solid rgba(14, 124, 123, 0.08)"
val CardBorderHover = "1px solid rgba(14, 124, 123, 0.2)"

// Animation durations
val TransitionFast = "0.15s cubic-bezier(0.4, 0, 0.2, 1)"
val TransitionMedium = "0.25s cubic-bezier(0.4, 0, 0.2, 1)"
val TransitionSlow = "0.4s cubic-bezier(0.4, 0, 0.2, 1)"

// Border radius - more fluid
val RadiusSmall = "12px"
val RadiusMedium = "20px"
val RadiusLarge = "28px"
val RadiusXLarge = "36px"
val RadiusFull = "9999px"
